import React from 'react'
import { Navigate, redirect, RouteObject, useSearchParams } from 'react-router-dom'
import loadable from '@loadable/component'

import { SiteUrls } from '@/routes/siteUrls'
import { isElectron } from '@/utils/electron/electronUtils'

// Layouts
const MainLayout = loadable(() => import('@/layouts/MainLayout'))
const OnPremiseMainLayout = loadable(() => import('@/layouts/OnPremiseMainLayout'))
const NotSignedInLayout = loadable(() => import('@/layouts/NotSignedInLayout'))

// Login (web only)
const LoginLayout = loadable(() => import('@/pages/Login/LoginLayout'))
const LoginMain = loadable(() => import('@/pages/Login/Login'))
const SignUp = loadable(() => import('@/pages/Login/SignUp'))
const RecoverPassword = loadable(() => import('@/pages/Login/RecoverPassword'))
const ExpiredUrl = loadable(() => import('@/pages/Login/ExpiredUrl'))
const ChangePassword = loadable(() => import('@/pages/Login/ChangePassword'))

// SSO (CBTP — MANU AI 진입, web only)
const SsoLanding = loadable(() => import('@/pages/SSO/SsoLanding'))
const SsoError = loadable(() => import('@/pages/SSO/SsoError'))
const CbtpLoginRedirect = loadable(() => import('@/pages/SSO/CbtpLoginRedirect'))

// Workspace
const Workspaces = loadable(() => import('@/pages/Workspace/List'))
const NoWorkspace = loadable(() => import('@/pages/Workspace/NoWorkspace'))
const JoinWorkspace = loadable(() => import('@/pages/Workspace/Join'))
const CreateWorkspace = loadable(() => import('@/pages/Workspace/Create'))
const WorkspaceMain = loadable(() => import('@/pages/Workspace/Main'))
const WorkspaceSettings = loadable(() => import('@/pages/Workspace/Settings'))
const WorkspaceNotificationSettings = loadable(() => import('@/pages/Workspace/NotificationSettings'))
const WorkspaceTaskList = loadable(() => import('@/pages/Workspace/WorkspaceTaskList'))
const WorkspaceMembers = loadable(() => import('@/pages/Workspace/Members'))
const WorkspaceTeamsMembers = loadable(() => import('@/pages/Workspace/TeamsMembers'))
const WorkspacePricing = loadable(() => import('@/pages/Workspace/Pricing'))
const Credit = loadable(() => import('@/pages/Workspace/Credit/WorkspaceCreditDetail'))
const MonthlyStats = loadable(() => import('@/pages/Workspace/Credit/MonthlyStatistics/MonthlyStatistics'))
const ResourceMonitoring = loadable(() => import('@/pages/Workspace/ResourceMonitoring/ResourceMonitoring'))
const DiskSpaceManagement = loadable(() => import('@/pages/Workspace/ResourceMonitoring/DiskSpaceManagement/DiskSpaceManagement'))
const RegisterModel = loadable(() => import('@/pages/Workspace/RegisterModel/Main'))
const UploadModel = loadable(() => import('@/pages/Workspace/RegisterModel/ExternalModel/Upload'))
const AutoDeploy = loadable(() => import('@/pages/AutoDeploy/AutoDeploy'))
const AutoDeployHistory = loadable(() => import('@/pages/AutoDeploy/AutoDeployHistory'))

// Project
const Projects = loadable(() => import('@/pages/Project/Projects'))
const ProjectMain = loadable(() => import('@/pages/Project/Main'))
const ProjectModelDownloadHistory = loadable(() => import('@/pages/Project/ModelDownloadHistory'))
const ProjectModelRetrainHistory = loadable(() => import('@/pages/Project/ModelRetrainHistory'))

// DataStorage
const Storage = loadable(() => import('@/pages/Storage/Storage'))
const AutoCollectedStorage = loadable(() => import('@/pages/Storage/AutoCollectedStorage'))
const UnusedStorageItems = loadable(() => import('@/pages/Storage/UnusedStorageItems'))

// DataCollection (web only)
const DataCollectionEndpoint = loadable(() => import('@/pages/DataCollection/DataCollectionEndpoints'))
const DataCollectionList = loadable(() => import('@/pages/DataCollection/CollectedData/DataCollectionList'))
const CollectedDataList = loadable(() => import('@/pages/DataCollection/CollectedData/CollectedDataList'))
const SchemaList = loadable(() => import('@/pages/DataCollection/CustomSchema/CollectionSchemas'))
const DataCollectionMain = loadable(() => import('@/pages/DataCollection/Main'))

// Pipeline
const RenewedPipeline = loadable(() => import('@/pages/RenewedPipeline/RenewedPipeline'))
const OpsPipeline = loadable(() => import('@/pages/RenewedPipeline/OpsPipeline'))

// Labeling
const LabelingDetail = loadable(() => import('@/pages/Labeling/Detail/DetailMerged'))
const TabularLabelingDetail = loadable(() => import('@/pages/Labeling/Tabular/TabularLabeling'))
const VideoLabelingDetail = loadable(() => import('@/pages/Labeling/Video/VideoLabeling'))

// Result
const ModelResult = loadable(() => import('@/pages/Result/Model/ModelResult'))
const ModelRetrainResult = loadable(() => import('@/pages/Result/ModelRetrain/ModelRetrainResult'))
const ValidationResult = loadable(() => import('@/pages/Result/Validation/ValidationResult'))
const InferenceResult = loadable(() => import('@/pages/Result/Inference/InferenceResult'))
const IeModelResult = loadable(() => import('@/pages/Result/Model/IeModelResult'))
const IeValidationResult = loadable(() => import('@/pages/Result/Validation/IeValidationResult'))
const IeInferenceResult = loadable(() => import('@/pages/Result/Inference/IeInferenceResult'))
const DataDriftResult = loadable(() => import('@/pages/Result/DataDrift/DataDriftResult'))
const ModelDriftResult = loadable(() => import('@/pages/Result/ModelDrift/ModelDriftResult'))
const ModelPerformanceResult = loadable(() => import('@/pages/Result/ModelPerformance/ModelPerformanceResult'))
const ModelRetrainEvaluationResult = loadable(() => import('@/pages/Result/ModelRetrainEvaluationResult/ModelRetrainEvaluationResult'))
const ModelPipelineInstanceResult = loadable(() => import('@/pages/Result/Inference/ModelPipelineInstanceResult'))
const ModelPipelineResult = loadable(() => import('@/pages/Result/ModelPipelineResult/ModelPipelineResult'))
const EDAResult = loadable(() => import('@/pages/Result/EDA/EDA'))
const TabularModelResult = loadable(() => import('@/pages/Result/Tabular/TabularModelResult'))
const TabularValidationResult = loadable(() => import('@/pages/Result/Tabular/TabularValidationResult'))
const TabularInferenceResult = loadable(() => import('@/pages/Result/Tabular/TabularInferenceResult'))
const VideoValidationResult = loadable(() => import('@/pages/Result/Video/VideoValidationResult'))
const VideoInferenceResult = loadable(() => import('@/pages/Result/Video/VideoInferenceResult'))
const VideoModelResult = loadable(() => import('@/pages/Result/Video/VideoModelResult'))

// User
const InquiryList = loadable(() => import('@/pages/User/Inquiries/List'))
const InquiryDetail = loadable(() => import('@/pages/User/Inquiries/Detail'))
const UserSettings = loadable(() => import('@/pages/User/UserSettings/UserSettings'))

// Manual — web: MainLayout 안 / electron: 정적 매뉴얼을 레이아웃 밖 최상위 라우트로 노출
const Manual = loadable(() => import('@/pages/Manual/Manual'))
const StaticManual = loadable(() => import('@/pages/Manual/StaticManual'))

const isCBTP = process.env.REACT_APP_USE_CBTP === 'true'

// CBTP 빌드 뒷문 — /login?entry=daisy-native 로 진입하면 MANU AI 리다이렉트 대신 기존 자체 로그인(LoginLayout) 노출
const CbtpLoginGate = () => {
  const [params] = useSearchParams()
  return params.get('entry') === 'daisy-native' ? <LoginLayout /> : <CbtpLoginRedirect />
}

/**
 * 웹/Electron(온프레미스) 공용 라우트.
 *
 * 분기 기준은 `isElectron()` 하나다. Electron은 웹의 부분집합이므로:
 *  - 공유 페이지는 그냥 배열에 한 번만 추가하면 양쪽에 자동 노출된다.
 *  - 한쪽에서만 보여야 하는 페이지만 `electron ? ... : ...` 스프레드로 감싼다.
 *
 * 새 노드/결과 페이지를 추가할 때, 양쪽에 다 필요하면 "공유" 섹션에만 넣으면 된다.
 */
const routes = (isSignIn?: boolean): RouteObject[] => {
  const electron = isElectron()

  // 환경 전용 라우트를 평탄하게 표시하기 위한 헬퍼 — 스프레드(...)로 펼쳐 쓴다.
  // 공유 라우트는 그냥 배열에 그대로 두고, 한쪽 전용만 web()/onPrem()으로 감싼다.
  const web = (...r: RouteObject[]): RouteObject[] => (electron ? [] : r)
  const onPrem = (...r: RouteObject[]): RouteObject[] => (electron ? r : [])

  // ── 워크스페이스 하위 라우트 ──
  const workspaceChildren: RouteObject[] = [
    // 공유
    { path: '', element: <Navigate to="projects" replace /> },
    { path: SiteUrls.Main.Workspace.Settings, element: <WorkspaceSettings /> },
    { path: SiteUrls.Main.Workspace.NotificationSettings, element: <WorkspaceNotificationSettings /> },
    { path: SiteUrls.Main.Workspace.RegisterModel._, element: <RegisterModel /> },
    { path: SiteUrls.Main.Workspace.RegisterModel.Upload, element: <UploadModel /> },
    { path: SiteUrls.Main.Workspace.Projects, element: <Projects /> },
    { path: SiteUrls.Main.Workspace.Storage._, element: <Storage /> },
    { path: SiteUrls.Main.Workspace.Storage.folder(':id'), element: <Storage /> },
    { path: SiteUrls.Main.Workspace.UnusedStorageItems._, element: <UnusedStorageItems /> },
    // electron 전용
    ...onPrem({ path: SiteUrls.Main.Workspace.WorkspaceTaskList, element: <WorkspaceTaskList /> }),
    // 웹 전용
    ...web(
      { path: SiteUrls.Main.Workspace.Members, element: <WorkspaceMembers /> },
      { path: SiteUrls.Main.Workspace.TeamsMembers, element: <WorkspaceTeamsMembers /> },
      { path: SiteUrls.Main.Workspace.Pricing, element: <WorkspacePricing /> },
      { path: SiteUrls.Main.Workspace.AutoCollectedStorage._, element: <AutoCollectedStorage /> },
      { path: SiteUrls.Main.Workspace.AutoCollectedStorage.folder(':id'), element: <AutoCollectedStorage /> },
      { path: SiteUrls.Main.Workspace.AutoDeploy._, element: <AutoDeploy /> },
      { path: SiteUrls.Main.Workspace.AutoDeploy.History(':id'), element: <AutoDeployHistory /> },
      { path: SiteUrls.Main.Workspace.ResourceMonitoring, element: <ResourceMonitoring /> },
      { path: SiteUrls.Main.Workspace.DiskSpaceManagement, element: <DiskSpaceManagement /> },
      { path: SiteUrls.Main.Workspace.DataCollection._, element: <DataCollectionMain /> },
      { path: SiteUrls.Main.Workspace.DataCollection.Endpoint, element: <DataCollectionEndpoint /> },
      { path: SiteUrls.Main.Workspace.DataCollection.Collections, element: <DataCollectionList isOwner={false} /> },
      { path: SiteUrls.Main.Workspace.DataCollection.CollectionDataList(':id'), element: <CollectedDataList /> },
      { path: SiteUrls.Main.Workspace.DataCollection.CollectionSchemas, element: <SchemaList /> },
      { path: SiteUrls.Main.Workspace.Credit._, element: <Credit /> },
      { path: SiteUrls.Main.Workspace.Credit.Stats, element: <MonthlyStats /> }
    ),
  ]

  // ── 프로젝트 하위 라우트 (노드 상세/결과 페이지) ──
  const projectChildren: RouteObject[] = [
    // 공유
    { path: '', element: <Navigate to="pipeline" replace /> },
    { path: SiteUrls.Main.Project.Pipeline(':projectId'), element: <RenewedPipeline /> },
    { path: SiteUrls.Main.Project.ModelDownloadHistory(':projectId'), element: <ProjectModelDownloadHistory /> },
    { path: SiteUrls.Main.Project.Labeling.Detail(':projectId', ':nodeId'), element: <LabelingDetail /> },
    { path: SiteUrls.Main.Project.Labeling.Video(':projectId', ':nodeId'), element: <VideoLabelingDetail /> },
    { path: SiteUrls.Main.Project.Result.Model(':projectId', ':nodeId'), element: <ModelResult /> },
    { path: SiteUrls.Main.Project.Result.ModelRetrain(':projectId', ':nodeId'), element: <ModelRetrainResult /> },
    { path: SiteUrls.Main.Project.Result.ModelIe(':projectId', ':nodeId'), element: <IeModelResult /> },
    { path: SiteUrls.Main.Project.Result.Validation(':projectId', ':nodeId'), element: <ValidationResult /> },
    { path: SiteUrls.Main.Project.Result.ValidationIe(':projectId', ':nodeId'), element: <IeValidationResult /> },
    { path: SiteUrls.Main.Project.Result.Inference(':projectId', ':nodeId'), element: <InferenceResult /> },
    { path: SiteUrls.Main.Project.Result.InferenceIe(':projectId', ':nodeId'), element: <IeInferenceResult /> },
    { path: SiteUrls.Main.Project.Result.ValidationVideo(':projectId', ':nodeId'), element: <VideoValidationResult /> },
    { path: SiteUrls.Main.Project.Result.InferenceVideo(':projectId', ':nodeId'), element: <VideoInferenceResult /> },
    { path: SiteUrls.Main.Project.Result.ModelVideo(':projectId', ':nodeId'), element: <VideoModelResult /> },
    // 웹 전용
    ...web(
      { path: SiteUrls.Main.Project.ModelRetrainHistory(':projectId'), element: <ProjectModelRetrainHistory /> },
      { path: SiteUrls.Main.Project.ModelManagement(':projectId'), element: <p>모델 관리</p> },
      { path: SiteUrls.Main.Project.Dashboard(':projectId'), element: <p>대시보드</p> },
      { path: SiteUrls.Main.Project.OpsPipeline(':projectId'), element: <OpsPipeline /> },
      { path: SiteUrls.Main.Project.Labeling.Tabular(':projectId', ':nodeId'), element: <TabularLabelingDetail /> },
      { path: SiteUrls.Main.Project.Result.DataDrift(':projectId', ':nodeId'), element: <DataDriftResult /> },
      { path: SiteUrls.Main.Project.Result.ModelDrift(':projectId', ':nodeId'), element: <ModelDriftResult /> },
      {
        path: SiteUrls.Main.Project.Result.ModelPerformance(':projectId', ':nodeId'),
        element: <ModelPerformanceResult />,
      },
      {
        path: SiteUrls.Main.Project.Result.ModelRetrainEvaluation(':projectId', ':nodeId'),
        element: <ModelRetrainEvaluationResult />,
      },
      {
        path: SiteUrls.Main.Project.Result.ModelPipelineInstance(':projectId', ':nodeId'),
        element: <ModelPipelineInstanceResult />,
      },
      {
        path: SiteUrls.Main.Project.Result.ModelPipelineResult(':projectId', ':nodeId'),
        element: <ModelPipelineResult />,
      },
      { path: SiteUrls.Main.Project.Result.EDA(':projectId', ':nodeId'), element: <EDAResult /> },
      { path: SiteUrls.Main.Project.Result.ModelTabular(':projectId', ':nodeId'), element: <TabularModelResult /> },
      {
        path: SiteUrls.Main.Project.Result.ValidationTabular(':projectId', ':nodeId'),
        element: <TabularValidationResult />,
      },
      {
        path: SiteUrls.Main.Project.Result.InferenceTabular(':projectId', ':nodeId'),
        element: <TabularInferenceResult />,
      }
    ),
  ]

  // ── Main 레이아웃 하위 직속 라우트 ──
  // 매칭 우선순위는 배열 순서가 아니라 경로 구체성(react-router v6)이 정하므로
  // 공유/웹 전용을 한 블록씩 모아도 동작은 동일하다.
  // (단 path: '' 두 개는 동순위라, 공유 Navigate가 웹 Projects보다 앞서야 함)
  const mainChildren: RouteObject[] = [
    // 공유
    { path: '', element: <Navigate to="workspace/projects" replace /> },
    { path: SiteUrls.Main.Workspace.Main, element: <WorkspaceMain />, children: workspaceChildren },
    { path: SiteUrls.Main.Project.Main(':projectId'), element: <ProjectMain />, children: projectChildren },
    { path: SiteUrls.Main.User.Inquiries, element: <InquiryList /> },
    { path: SiteUrls.Main.User.InquiryDetail(':id'), element: <InquiryDetail /> },
    // 웹 전용
    ...web(
      { path: SiteUrls.Main.Workspace.Join, element: <JoinWorkspace /> },
      { path: SiteUrls.Main.Workspace.Create, element: <CreateWorkspace /> },
      { path: SiteUrls.Main.Workspace.NoWorkspace, element: <NoWorkspace /> },
      { path: SiteUrls.Main.Workspace.List, element: <Workspaces /> },
      { path: SiteUrls.Main.Workspace.Invited(':invitationCode'), element: <WorkspaceMain /> },
      { path: SiteUrls.Main.User.Settings, element: <UserSettings /> },
      { path: SiteUrls.Main.Manual.List, element: <Manual /> },
      { path: '', element: <Projects /> }
    ),
  ]

  // 최상위도 경로가 모두 distinct하므로 웹 전용 라우트를 한 블록으로 모은다.
  return [
    // 웹 전용 — 로그인 / 랜딩 + SSO 도착지(로그인 화면과 무관하게 항상 동작)
    ...web(
      {
        path: SiteUrls.Landing,
        element: <NotSignedInLayout />,
        children: [
          { path: '', element: <Navigate to={SiteUrls.Login._} replace /> },
          {
            path: SiteUrls.Login._,
            // CBTP는 MANU AI 로그인으로 일원화 — 자체 로그인 화면 비노출 (FR-006)
            element: isCBTP ? <CbtpLoginGate /> : <LoginLayout />,
            children: [
              { path: SiteUrls.Login._, element: <LoginMain /> },
              { path: SiteUrls.Login.SignUp, element: <SignUp /> },
              { path: SiteUrls.Login.RecoverPassword, element: <RecoverPassword /> },
              { path: SiteUrls.Login.ExpiredInvitation, element: <ExpiredUrl /> },
              { path: SiteUrls.Login.ChangePassword, element: <ChangePassword /> },
            ],
          },
        ],
      },
      { path: SiteUrls.Sso.Landing, element: <SsoLanding /> },
      { path: SiteUrls.Sso.Error, element: <SsoError /> }
    ),
    // electron 전용 — 정적 매뉴얼 (레이아웃 밖 최상위)
    ...onPrem({ path: '/manual/list', element: <StaticManual /> }),
    // 공유 — 메인 레이아웃 (전체 앱)
    {
      path: SiteUrls.Main._,
      element: electron ? <OnPremiseMainLayout /> : <MainLayout />,
      children: mainChildren,
    },
    // 공유 — fallback
    {
      path: '*',
      element: <Navigate to={electron ? SiteUrls.Main.Workspace.Main : SiteUrls.Main.Workspace.NoWorkspace} replace />,
    },
  ]
}

export default routes
